import QualityMentoringFormQuestion from './models/QualityMentoringFormQuestion';
import QuestionType from './models/QuestionType';
import DisplayRequirements from './models/DisplayRequirements';
import RequirementsMetPolicy from './RequirementsMetPolicy';

export const SKIPPED = '::SKIPPED';

function requirementCanBeSatisfied(submitted, path, v): boolean {
  if (submitted[path] === undefined) {
    return true;
  }
  if (submitted[path] === SKIPPED) {
    return false;
  }
  if (v === null && submitted[path] && submitted[path] != '') {
    return true;
  }
  if (submitted[path] !== v) {
    return false;
  }
  return true;
}

function multipleSelectionRequirementCanBeSatisfied(submitted, path, v): boolean {
  if (submitted[path] === undefined) {
    return true;
  }
  if (!Object.keys(submitted[path]).length) {
    return true;
  }
  const notSkipped = Object.entries(submitted[path]).filter((value) => value[1] !== SKIPPED);
  if (!notSkipped.length) {
    return false;
  }
  const answers = Object.entries(submitted[path]).filter((value) => value[1] !== false && value[1] !== SKIPPED);
  if (answers.length && !v) {
    return true;
  }
  if (!answers.length) {
    return true;
  }
  if (submitted[path][v] === false) {
    return false;
  }
  if (submitted[path][v] === SKIPPED) {
    return false;
  }
  if (submitted[path][v]) {
    return true;
  }
  return false;
}

function questionHasFailedToMeetRequirements(requirements: DisplayRequirements, submission): boolean {
  const groups = Array.isArray(requirements.list) ? requirements.list : [requirements.list];

  let failedCount = 0;

  groups.forEach((group) => {
    if (group.operator) {
      if (questionHasFailedToMeetRequirements(group, submission)) {
        failedCount += 1;
      }
    } else {
      if (!requirementGroupCanBeSatisfied(group, submission)) {
        // no operator: defaulting to OR
        failedCount += 1;
      }
    }
  });

  // or behavior
  if (requirements.operator === 'OR') {
    return failedCount === groups.length;
  }

  return failedCount > 0;
}

function requirementGroupCanBeSatisfied(requirementGroup, submission): boolean {
  const requirements = Object.entries(requirementGroup);

  const unsatisfactory = requirements.filter((value) => {
    const index = value[0];
    if (index === 'function') {
      const functionName: any = value[1];
      return !RequirementsMetPolicy[functionName](submission);
    }

    const satisfactoryValues = Array.isArray(value[1]) ? value[1] : [value[1]];
    let unsatisfactoryInput = [];
    if (typeof submission.root[index] === 'object') {
      unsatisfactoryInput = satisfactoryValues.filter((v) => multipleSelectionRequirementCanBeSatisfied(submission.root, index, v) === false);
    } else {
      unsatisfactoryInput = satisfactoryValues.filter((v) => requirementCanBeSatisfied(submission.root, index, v) === false);
    }
    return unsatisfactoryInput.length === satisfactoryValues.length;
  });

  return unsatisfactory.length < requirements.length;
}

function isAnswerable(question, submission): boolean {
  if (!question.requires) {
    return true;
  }

  if (questionHasFailedToMeetRequirements(question.requires, submission)) {
    return false;
  }

  return true;
}

export function getAnswered(questions, submission): QualityMentoringFormQuestion[] {
  return questions.filter((question: QualityMentoringFormQuestion) => {
    switch (question.type) {
      case QuestionType.Rank:
      case QuestionType.Info:
      case QuestionType.Complete:
      case QuestionType.Unsure1A3:
      case QuestionType.Unsure:
        return false;
      case QuestionType.Slider:
        return submission.data.root?.[question.path] !== 0 && submission.data.root?.[question.path] !== undefined;
      case QuestionType.MultipleChoice:
        return submission.data.root?.[question.path];
      case QuestionType.TextEntry:
      case QuestionType.TextareaEntry:
        return question.options
          .map((option) => submission.data.root?.[option.value])
          .filter((option) => option)
          .length;
      case QuestionType.MultipleChoiceTable:
      case QuestionType.MultipleSelectionTable:
      case QuestionType.MultipleSelection: {
        const result = question.options
          .map((option) => {
            if (submission.data.root?.[question.path]) {
              return submission.data.root[question.path][option.value];
            }
          })
          .filter((value) => value)
          .length;
        return result;
      }
      default: {
        const message = `not implemented for ${question.type}`;
        console.error(message);
      }
    }
  });
}

export function getUnanswered(questions, submission, getWithCalloutBlocks = false): QualityMentoringFormQuestion[] {
  return questions.filter((question: QualityMentoringFormQuestion) => {
    switch (question.type) {
      case QuestionType.Rank:
        return false;
      case QuestionType.Info:
      case QuestionType.Complete:
      case QuestionType.Unsure1A3:
      case QuestionType.Unsure:
        return getWithCalloutBlocks;
      case QuestionType.Slider:
        return submission.data.root?.[question.path] === 0 || submission.data.root?.[question.path] === undefined;
      case QuestionType.MultipleChoice:
        return submission.data.root?.[question.path] === undefined;
      case QuestionType.TextEntry:
      case QuestionType.TextareaEntry:
        return question.options
          .map((option) => submission.data.root?.[option.value] || null)
          .filter((option) => option === null)
          .length === question.options.length;
      case QuestionType.MultipleSelectionTable:
      case QuestionType.MultipleSelection: {
        return question.options
          .map((option) => {
            if (submission.data.root?.[question.path]) {
              return submission.data.root?.[question.path][option.value];
            }
          })
          .filter((value) => value)
          .length === 0;
      }
      case QuestionType.MultipleChoiceTable: {
        if (!submission.data.root?.[question.path]) {
          return true;
        }
        return !Object.values(submission.data.root[question.path])?.length;
      }
      default: {
        const message = `not implemented for ${question.type}`;
        console.error(message);
      }
    }
  });
}

function getAnswerable(questions, submission): QualityMentoringFormQuestion[] {
  return questions.filter((question: QualityMentoringFormQuestion) => {
    if (question.type === QuestionType.MultipleChoiceTable
      || question.type === QuestionType.MultipleSelection
      || question.type === QuestionType.MultipleSelectionTable) {
      if (!submission.data.root[question.path]) {
        return true;
      }
      const submitted = Object.entries(submission.data.root[question.path]).filter((value) => {
        return value[1];
      });
      if (!submitted.length) {
        return true;
      }
      const filtered = submitted.filter((value) => {
        return value[1] !== SKIPPED;
      });
      if (!filtered.length) {
        return false;
      }
      return filtered.length === question.options.length;
    } else {
      return submission.data.root[question.path] !== SKIPPED;
    }
  });
}

function getSnapshot(questions, submission) {
  const submissionCopy = JSON.parse(JSON.stringify(submission));
  if (!submissionCopy.data.root) submissionCopy.data.root = {};

  const unanswered = getUnanswered(questions, submissionCopy);

  /* treat partial answers as complete */
  const textEntries = questions.filter((question) => question.type === QuestionType.TextEntry || question.type === QuestionType.TextareaEntry);
  const textEntriesOptions = textEntries.map((question) => question.options);
  textEntriesOptions.forEach((options) => {
    const notEmpty = options.filter((option) => submissionCopy.data.root[option.value]);
    if (notEmpty.length) {
      const empty = options.filter((option) => !submissionCopy.data.root[option.value]);
      empty.forEach((option) => {
        submissionCopy.data.root[option.value] = SKIPPED;
      });
    }
  });

  /* treat partial answers as complete */
  const multipleSelections = questions.filter((question) => question.type === QuestionType.MultipleSelection || question.type === QuestionType.MultipleSelectionTable);
  multipleSelections.forEach((question) => {
    if (!submissionCopy.data.root[question.path]) {
      submissionCopy.data.root[question.path] = {};
    }
    const notEmpty = question.options.filter((option) => submissionCopy.data.root[question.path][option.value]);
    if (notEmpty.length) {
      const empty = question.options.filter((option) => !submissionCopy.data.root[question.path][option.value]);
      empty.forEach((option) => {
        submissionCopy.data.root[question.path][option.value] = SKIPPED;
      });
    }
  });

  unanswered.forEach((question: QualityMentoringFormQuestion) => {
    const answerable = isAnswerable(question, submissionCopy.data);
    if (!answerable) {
      switch (question.type) {
        case QuestionType.Slider:
        case QuestionType.MultipleChoice:
          submissionCopy.data.root[question.path] = SKIPPED;
          break;
        case QuestionType.TextEntry:
        case QuestionType.TextareaEntry:
          submissionCopy.data.root[question.path] = SKIPPED;
          question.options.forEach((option) => {
            submissionCopy.data.root[option.value] = SKIPPED;
          });
          break;
        case QuestionType.Rank:
        case QuestionType.MultipleSelectionTable:
        case QuestionType.MultipleChoiceTable:
        case QuestionType.MultipleSelection: {
          question.options.forEach((option) => {
            if (!submissionCopy.data.root[question.path]) {
              submissionCopy.data.root[question.path] = {};
            }
            submissionCopy.data.root[question.path][option.value] = SKIPPED;
          });
          break;
        }
        default:
          console.error(`${question.type} not implemented`);
          break;
      }
    }
  });
  return submissionCopy;
}

export default function getPercentComplete(questions, submission, snapshotQuestions = null, pageNumber = null): number {
  const answered = getAnswered(questions, submission);
  let unanswered = getUnanswered(questions, submission);

  const getRequirementGroups = (requirements: DisplayRequirements, result = []) => {
    requirements.list?.forEach((a) => {
      if(a.list) {
        getRequirementGroups(a, result);
      } else {
        result.push(a);
      }
    });
    return result;
  };

  if (pageNumber !== null) {
    pageNumber += 1;
    unanswered = unanswered.filter((question) => {
      if (!question.requires) {
        return true;
      }

      const groups = getRequirementGroups(question.requires);
      const requires = groups.reduce((result, a) => {
        return { ...result, ...a };
      }, {});

      let pagesRequired = Object.keys(requires);
      pagesRequired = pagesRequired.map((a) => {
        return a.charAt(0);
      });
      pagesRequired = pagesRequired.filter(a => a !== 'f');

      const requiresExternalPage = pagesRequired.reduce((result, a) => {
        return result || (parseInt(a) !== pageNumber);
      }, false);

      if (requiresExternalPage) {
        const externalQuestionKeys = Object.keys(requires).filter((a) => {
          return parseInt(a.charAt(0)) !== pageNumber && a.charAt(0) !== 'f';
        });

        const unansweredExternalRequirements = externalQuestionKeys.filter((a) => {
          if (!submission.data.root) {
            return true;
          }
          return submission.data.root[a] === undefined;
        });

        if (!unansweredExternalRequirements.length) {
          return true;
        }
      }

      return !requiresExternalPage;
    });
  }

  const snapshot = getSnapshot(snapshotQuestions || questions, submission);
  const answerable = getAnswerable(unanswered, snapshot);

  const answerableTotal = answered.length + answerable.length;
  return Math.floor((answered.length / answerableTotal) * 100) || 0;
}
